export interface EmailSections {
  body: string;
  signature?: string;
  previous?: string;
}

// In a single-line text (older stored messages) a marker may match anywhere; in a multi-line text it must start a line,
// so quoted text a customer pastes mid-message is not collapsed.
const OUTLOOK_FROM = /\b(?:Fra|From):/g;
const OUTLOOK_SENT_WITHIN_300 = /^[^]{0,300}?\b(?:Sendt|Sent):/;
const OUTLOOK_SEPARATOR_BEFORE = /_{4,}\s*$/;
// "On <date>, Name <a@b> wrote:" -- the <address> is not required because stripEmailMarkup removes it as an HTML tag.
// The span may not contain another "On ", so an earlier sentence starting with "On" stays in the body.
const GMAIL_EN = /\bOn\s(?:(?!\bOn\s)[^\n]){0,200}?\bwrote:/g;
// "<date> skrev Name <a@b>:" -- the <address> is not required because stripEmailMarkup removes it as an HTML tag.
// Without it "skrev ...:" is ordinary prose, so gmailNoStart also needs a date before it or a line start.
const GMAIL_NO = /\bskrev\s[^\n:]{0,200}?:/g;
// Date right before "skrev", e.g. "man. 29. sep. 2026 kl. 10:00 " or "29.09.2026 10:00 ".
const GMAIL_NO_DATE_BEFORE = /(?:\p{L}{2,4}\.?\s+)?\d{1,2}\.\s*(?:\p{L}{3,9}\.?|\d{1,2}\.)\s*\d{4}(?:,?\s+(?:kl\.?\s*)?\d{1,2}[:.]\d{2})?[,\s]*$/u;
const ORIGINAL_MESSAGE = /-{2,}\s*(?:Original Message|Opprinnelig melding)\s*-{2,}/i;
const SIGNATURE = /\b(?:Med vennlig hilsen|Vennlig hilsen|Mvh|Best regards|Kind regards|DISCLAIMER|Sent from my|Sendt fra min)\b/i;

/**
 * Splits a plain-text email into the reply body, its signature and the quoted previous email.
 * Falls back to the whole text as body when nothing would be left in the body, so no text is ever hidden without a reply.
 */
export function splitEmailSections(text: string): EmailSections {
  const previousStart = findPreviousStart(text);
  const signatureStart = text.slice(0, previousStart).search(SIGNATURE);
  const bodyEnd = signatureStart >= 0 ? signatureStart : previousStart;

  const body = text.slice(0, bodyEnd).trim();
  if (!body) return { body: text.trim() };

  const signature = text.slice(bodyEnd, previousStart).trim();
  const previous = text.slice(previousStart).trim();
  return {
    body,
    ...(signature ? { signature } : {}),
    ...(previous ? { previous } : {}),
  };
}

function findPreviousStart(text: string): number {
  const starts = [outlookHeaderStart(text), gmailEnStart(text), gmailNoStart(text), text.search(ORIGINAL_MESSAGE)]
    .filter(index => index >= 0);
  return starts.length ? Math.min(...starts) : text.length;
}

function isLineStart(text: string, index: number): boolean {
  return text.slice(text.lastIndexOf('\n', index - 1) + 1, index).trim() === '';
}

function isHeaderPosition(text: string, index: number): boolean {
  return !text.includes('\n') || isLineStart(text, index);
}

function outlookHeaderStart(text: string): number {
  for (const from of text.matchAll(OUTLOOK_FROM)) {
    if (!isHeaderPosition(text, from.index)) continue;
    if (!OUTLOOK_SENT_WITHIN_300.test(text.slice(from.index + from[0].length))) continue;
    const separator = text.slice(0, from.index).match(OUTLOOK_SEPARATOR_BEFORE);
    return separator ? from.index - separator[0].length : from.index;
  }
  return -1;
}

function gmailEnStart(text: string): number {
  for (const on of text.matchAll(GMAIL_EN)) {
    if (isHeaderPosition(text, on.index)) return on.index;
  }
  return -1;
}

function gmailNoStart(text: string): number {
  for (const skrev of text.matchAll(GMAIL_NO)) {
    const date = text.slice(0, skrev.index).match(GMAIL_NO_DATE_BEFORE);
    const start = date ? skrev.index - date[0].length : skrev.index;
    if (date ? isHeaderPosition(text, start) : isLineStart(text, start)) return start;
  }
  return -1;
}
